import React from "react";
import { ChevronRight } from "lucide-react";
import { Button } from "../../atoms/Button/Button.js";

/* ---------------- DataTableTreeCell ----------------
   The first data cell of a tree row (`getSubRows`): the row's indent, its
   expand/collapse toggle and then the column's own content.

   The indent is 16px per level below the first, on the logical start side, so
   it mirrors under `dir="rtl"`. A parent carries the 24px ghost xs Button with
   the one caret that turns (`pb-disclosure-caret`, ADR-0091); a leaf keeps a
   24px spacer in its place, so siblings' content lines up whether or not they
   have children. The toggle's negative block margin keeps its 24px box inside
   the row's own line height, so a tree row is as tall as any other row.

   The row, not the button, carries `aria-expanded` (APG treegrid); the
   button's name says what it will do: "Expand {row}" or "Collapse {row}". */

export interface DataTableTreeCellProps {
  /** 1-based depth of the row. */
  readonly level: number;
  readonly hasChildren: boolean;
  readonly expanded: boolean;
  /** The row's name, for the toggle's accessible name. */
  readonly label: string;
  readonly onToggle: () => void;
  /** -1 inside the keyboard grid, which owns the only tab stop. */
  readonly tabIndex: number | undefined;
  readonly children: React.ReactNode;
}

export function DataTableTreeCell({
  level,
  hasChildren,
  expanded,
  label,
  onToggle,
  tabIndex,
  children,
}: DataTableTreeCellProps): React.ReactElement {
  return (
    <div
      className="flex items-center gap-1"
      style={{ paddingInlineStart: `${(level - 1) * 16}px` }}
    >
      {hasChildren ? (
        <Button
          type="button"
          variant="ghost"
          iconOnly
          size="xs"
          aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`}
          onClick={onToggle}
          className="-my-0.5 shrink-0"
          {...(tabIndex !== undefined ? { tabIndex } : {})}
        >
          <ChevronRight
            aria-hidden="true"
            data-expanded={expanded ? "true" : "false"}
            className="pb-disclosure-caret h-3.5 w-3.5"
          />
        </Button>
      ) : (
        <span aria-hidden="true" className="w-6 shrink-0" />
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
